



















































































































import { mapActions, mapGetters, mapState } from 'vuex';
import type { PropType } from 'vue';

import Tooltip from '@/components/tooltip/Tooltip.vue';
import ColorPicker from '@/components/core/ColorPicker.vue';

import type {
  StakingProgram,
  NewStakingProgramData,
  ProgramDialogData,
  SelectItem
} from '@/api/schema';
import { StakingProgramType } from '@/api/schema';
import {
  getStackingProgramPromoCampaigns,
  updateStackingProgramPromoCampaigns
} from '@/api/StakingPrograms';
import { getLoyaltyLevelBasedPromoCampaigns } from '@/api/PromoCampaign';

import { ELoyaltyFeatureFlag } from '@/store/modules/feature-flags/FeatureFlagNames';

import * as vr from '@/helpers/validation';
import { fromPercents, toPercents } from '@/helpers/decimalsHelpers';
import { errorToastMessage } from '@/helpers/errorToastMessage';

const defaultProgramData = {
  type: StakingProgramType.GENERAL,
  name: '',
  rate: 0,
  minAmount: 0,
  maxAmount: 0,
  durationSeconds: 0,
  status: 'ACTIVE',
  limited: false
};

export default {
  name: 'StakingFormDialog',
  components: {
    Tooltip,
    ColorPicker
  },
  props: {
    value: {
      type: Boolean as PropType<boolean>,
      default: (): boolean => false
    },
    programItem: {
      type: Object as PropType<StakingProgram>,
      default: (): StakingProgram => ({} as StakingProgram)
    },
    operation: {
      type: String as PropType<string>,
      default: (): string => ''
    },
    data: {
      type: Object as PropType<ProgramDialogData>,
      default: (): ProgramDialogData => {
        return {} as ProgramDialogData;
      }
    }
  },
  data(): any {
    return {
      programData: {
        ...defaultProgramData
      },
      programColor: null,
      availablePromoCampaigns: [],
      relatedPromoCampaignsId: []
    };
  },
  computed: {
    ...mapState('Auth', ['tokenSymbol']),
    ...mapState('StakingPrograms', ['programs']),
    ...mapState('app', ['isSuperAdmin']),
    ...mapGetters('FeatureFlags', ['isEnabledFeatureFlag']),
    canEdit(): boolean {
      return (
        !this.programData.id ||
        this.$role.is(['account', 'admin']) ||
        this.isSuperAdmin
      );
    },
    programTypes(): SelectItem[] {
      return [
        {
          text: 'General Hold to Earn Program',
          value: StakingProgramType.GENERAL,
          disabled: !!this.programData.id
        },
        {
          text: 'Welcome Hold to Earn',
          value: StakingProgramType.WELCOME,
          disabled: !!this.programData.id || this.hasWelcomeStakingProgram
        }
      ];
    },
    header(): string {
      return this.data?.header || '';
    },
    buttonText(): string {
      return this.data?.buttonText || '';
    },
    isModalVisible: {
      get(): boolean {
        return this.value;
      },
      set(value: boolean): void {
        this.$emit('input', value);
      }
    },
    programRate: {
      get(): number {
        return toPercents(this.programData.rate);
      },
      set(value: number): void {
        this.programData.rate = fromPercents(value);
      }
    },
    formRules(): any {
      return {
        name: [(v) => vr.maxLength(v, 50)],
        period: [vr.required, vr.validNumber, vr.positiveNumber, vr.integer],
        rate: [
          vr.required,
          vr.validNumber,
          vr.positiveNumber,
          (v) => vr.gte(v, 0.1),
          (v) => vr.lte(v, 100)
        ],
        minAmount: [vr.required, vr.validNumber, vr.positiveNumber],
        maxAmount: [
          (v) => (v ? vr.validNumber(v) : true),
          (v) => (v ? vr.positiveNumber(v) : true),
          (v) => (v ? vr.gte(v, this.programData.minAmount) : true)
        ],
        programColor: [vr.required, vr.hexColor]
      };
    },
    hasWelcomeStakingProgram(): boolean {
      return this.programs.some(
        (item) =>
          item.status !== 'DELETE' && item.type === StakingProgramType.WELCOME
      );
    },
    isGeneralStakingProgram(): boolean {
      return this.programData.type === StakingProgramType.GENERAL;
    },
    isWelcomeStakingProgram(): boolean {
      return this.programData.type === StakingProgramType.WELCOME;
    },
    isLoyaltyLevelRestrictionEnabled(): boolean {
      return (
        this.operation === 'update' &&
        this.isEnabledFeatureFlag(
          ELoyaltyFeatureFlag.HOLD_TO_EARN_LOYALTY_LEVEL_RESTRICTION
        )
      );
    }
  },
  watch: {
    async programItem(newValue: StakingProgram): Promise<void> {
      if (newValue) {
        this.$refs.form?.reset();
        await this.$nextTick();

        this.programData = {
          ...defaultProgramData,
          ...newValue
        };
        this.programColor =
          (newValue?.colors && newValue?.colors[1]) || '#F8964C';
      }

      if (this.isLoyaltyLevelRestrictionEnabled) {
        try {
          const [
            loyaltyLevelBasedPromoCampaigns,
            stackingProgramPromoCampaigns
          ] = await Promise.all([
            getLoyaltyLevelBasedPromoCampaigns(),
            getStackingProgramPromoCampaigns(newValue.id)
          ]);

          this.availablePromoCampaigns = loyaltyLevelBasedPromoCampaigns.map(
            (item) => {
              return {
                text: item.name,
                value: item.id
              };
            }
          );
          this.relatedPromoCampaignsId = stackingProgramPromoCampaigns.map(
            (item) => item.id
          );
        } catch (error) {
          console.error(error);
          this.availablePromoCampaigns = [];
          this.relatedPromoCampaignsId = [];
        }
      }
    }
  },
  methods: {
    ...mapActions('StakingPrograms', [
      'createStakingProgram',
      'updateStakingProgram'
    ]),
    async handleOnSubmit(): Promise<void> {
      const isFormValid = await this.$refs.form?.validate();
      if (!isFormValid) {
        return;
      }

      try {
        if (this.operation === 'update') {
          const requests = [];

          requests.push(
            this.updateStakingProgram({
              id: this.programData.id,
              data: this.createSubmitData()
            })
          );

          if (this.isLoyaltyLevelRestrictionEnabled) {
            requests.push(
              updateStackingProgramPromoCampaigns({
                id: this.programData.id,
                body: this.relatedPromoCampaignsId
              })
            );
          }

          await Promise.all(requests);
        } else {
          await this.createStakingProgram(this.createSubmitData());
        }

        this.closeModal();
      } catch (error) {
        console.error(error);
        errorToastMessage(error);
      }
    },
    createSubmitData(): NewStakingProgramData {
      return {
        type: this.programData.type,
        minAmount: this.programData.minAmount || 0,
        maxAmount: this.programData.maxAmount || null,
        name: this.programData.name,
        rate: this.programData.rate,
        status: this.programData.status,
        durationSeconds: this.programData.durationSeconds,
        colors: [this.programColor, this.programColor],
        ...(this.isGeneralStakingProgram && {
          limited: this.programData.limited
        })
      };
    },
    closeModal(): void {
      this.$emit('close');
    }
  }
};
